import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import ErrorMessageWithIcon from "~/components/forms/ErrorMessageWithIcon";
import { hasPhysicalKeyboard, isMac } from "../lib/platformUtils";
import { ModalStates } from "../states/ModalStates";
import Feedback from "./feedback/Feedback";
import TokensUsage from "./feedback/TokensUsage";
import CommissionTierNotice from "./library/CommissionTierNotice";
import InlineToolbar from "./toolbar/InlineToolbar";

const InitialState = ({
  state,
  dispatch,
  handleInputChange,
  handleGenerateText,
  handleStop,
  handleInsert,
  handleRegenerate,
  handleNewPrompt,
  handleRetry,
  leadId,
  editorType,
  mode,
  selectedTier,
  onTierChange,
  contextDepth,
  onContextDepthChange,
  outputFormat,
  onOutputFormatChange,
  showFormatToggle,
  toggleVoiceInput,
  templateFlags,
}) => {
  const isGenerating = state.status === ModalStates.GENERATING;
  const isFinished = state.status === ModalStates.FINISHED;
  const isError = state.status === ModalStates.ERROR || !!state.message;
  const hasLimitError =
    state.conversationLimitExceeded || state.subscriptionLimitExceeded;
  const canSend = state.inputText?.trim().length > 0 && !hasLimitError;

  const handleKeyDown = (e) => {
    if (!hasPhysicalKeyboard) {
      return;
    }

    const mod = isMac ? e.metaKey : e.ctrlKey;

    if (e.key === "Enter" && !e.shiftKey && !mod) {
      e.preventDefault();
      if (!isGenerating && canSend) {
        handleGenerateText();
      }
    }

    if (isFinished && mod && e.key === "Enter" && e.shiftKey) {
      e.preventDefault();
      if (!state.feedbackSending && !hasLimitError) {
        handleRegenerate();
      }
    } else if (isFinished && mod && e.key === "Enter") {
      e.preventDefault();
      handleInsert();
    }

    if (isFinished && e.key === "Escape") {
      e.preventDefault();
      if (!state.feedbackSending) {
        handleNewPrompt();
      }
    }
  };

  return (
    <div className="form-group mb-2">
      <div className={`border rounded ${hasLimitError ? "border-danger" : ""}`}>
        <textarea
          className="form-control border-0 shadow-none"
          id="prompt"
          placeholder={
            isFinished
              ? "Tell me what to change ..."
              : "Type what to write about ..."
          }
          rows="5"
          value={state.inputText}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          readOnly={isGenerating}
        />

        <InlineToolbar
          status={state.status}
          leadId={leadId}
          editorType={editorType}
          mode={mode}
          selectedTier={selectedTier}
          onTierChange={onTierChange}
          showModelSelector={state.showModelSelector}
          conversationId={state.conversationId}
          contextDepth={contextDepth}
          onContextDepthChange={onContextDepthChange}
          contextLoading={state.contextLoading}
          outputFormat={outputFormat}
          onOutputFormatChange={onOutputFormatChange}
          showFormatToggle={showFormatToggle}
          canSend={canSend}
          hasLimitError={hasLimitError}
          feedbackSending={state.feedbackSending}
          onGenerate={handleGenerateText}
          onStop={handleStop}
          onInsert={handleInsert}
          onRegenerate={handleRegenerate}
          onNewPrompt={handleNewPrompt}
          onRetry={handleRetry}
          voiceState={state.voiceState}
          voiceSupported={state.voiceSupported}
          onToggleVoice={toggleVoiceInput}
          templateFlags={templateFlags}
        />
      </div>

      <CommissionTierNotice
        show={
          state.activePromptCategory === "commission" &&
          selectedTier === "reasoning" &&
          (state.inputText?.trim().length ?? 0) > 0
        }
      />

      {state.tierFallbackNotice && (
        <div className="mt-1 small text-info">
          <FontAwesomeIcon icon="far fa-info-circle" className="mr-1" />
          {state.tierFallbackNotice}
        </div>
      )}

      {state.voiceError && (
        <div className="mt-1">
          <ErrorMessageWithIcon message={state.voiceError} />
        </div>
      )}

      {state.inputError && (
        <div className="mt-1">
          <ErrorMessageWithIcon message={state.inputError} />
        </div>
      )}

      <div className="d-flex flex-wrap align-items-start justify-content-between mt-1">
        <TokensUsage state={state} />

        {isFinished && state.conversationId && (
          <Feedback state={state} dispatch={dispatch} />
        )}
      </div>
    </div>
  );
};

export default InitialState;
